import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Chips from 'components/field/Chips';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import useSallaSync from 'pages/SalesChannels/useSallaSync';

import { AUTOCOMPLETE, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { PLATFORM } from 'utils/constants';

import { faRefresh } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const SynchronizeForm = NiceModal.create(({ storeName, credentialsID, salesChannel }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { fetchOrdersWithIDs } = useSallaSync();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderIDs: yup.array().nullable().required(),
					storeName: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderIDs: null,
			storeName
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isSubmitting || isFormLoading;

	const onSubmit = async (data) => {
		try {
			if (
				import.meta.env.VITE_PLATFORM === PLATFORM.desktop &&
				((salesChannel?.slug === 'salla-automation' && import.meta.env.VITE_PLATFORM === PLATFORM.desktop) ||
					salesChannel?.slug === 'salla-desktop')
			) {
				const result = await fetchOrdersWithIDs(data.orderIDs, credentialsID, salesChannel?.merchantId);
				if (result?.successCount > 0 && result?.errorCount === 0) {
					showSuccess({
						header: 'syncOrderSuccess',
						message: 'syncOrderSuccessMessage'
					});
					modal.remove();
				} else {
					showError({
						header: 'syncOrderError',
						message: 'syncOrderErrorMessage'
					});
				}
			} else {
				const response = await axiosPrivate.post(ORDER_ACTIONS.SYNCHRONIZE_ORDER, {
					...data,
					credentialsID
				});
				if (response.status === 200) {
					showSuccess({
						header: 'syncOrderSuccess',
						message: 'syncOrderSuccessMessage'
					});
					modal.remove();
				} else {
					AppLogger.error(response);
				}
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="x-small" fullwidth className="modal-synchronizeOrder">
			<ModalTitle>
				<Typography variant="p1">synchronizeOrder</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderID" isLoading={isLoading}>
						<Chips
							name="orderIDs"
							value={watch('orderIDs')}
							setValue={(value) => {
								setValue(`orderIDs`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="storeNameTitle" isLoading={isLoading} required>
						<AutoComplete
							name="storeName"
							autoCompleteName={AUTOCOMPLETE.SALES_CHANNELS}
							textValue={watch('storeName')}
							setTextValue={(value) => {
								setValue(`storeName`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							setValue={(value) => {
								setValue(`storeName`, value?.Name ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							columns={[{ accessor: 'Name' }]}
							selectionLabelAccessor={['Name']}
							disabled={isLoading}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" onClick={() => modal.remove()} variant="outline" />
				<Button
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faRefresh}
					label="synchronize"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default SynchronizeForm;
